/**
 * The boss fight's arena (the simulation is game/boss.ts). A full screen over
 * the era's own painting, with the stream along the bottom: the dinosaur on
 * the bank at the left (in the water, if it swims), the boss in the water at
 * the right. Health, hearts and the clock across the top; Brace, the special
 * and Flee along the bottom. Taps on the canvas become hits, weak-point
 * crits, struggles out of a Death Roll, or a pick among the ripples.
 */

import type { BossDef } from '../data/bosses';
import type { DinoDef } from '../data/dinos';
import { artSizeOf } from '../data/dinos';
import {
  COMBO_MAX,
  HEARTS,
  METER_FULL,
  SPECIALS,
  bracing,
  brace,
  drainEvents,
  flee,
  specialReady,
  startFight,
  tapBoss,
  tapRipple,
  tickFight,
  useSpecial,
} from '../game/boss';
import type { Fight, FightEvent, FightOutcome } from '../game/boss';
import { TICKS_PER_SECOND } from '../game/loop';
import { eraModifier } from '../game/rebirth';
import { isEvolved, speciesName, spriteIdFor } from '../game/inventory';
import { formatEggs } from '../game/numbers';
import type { GameState } from '../game/state';
import type { SoundName } from './sound';
import {
  backgroundGroundColor,
  backgroundImage,
  inkBoundsOf,
  outlineOf,
  silhouetteOf,
  sizedDinoSprite,
  waterImage,
} from './sprites';
import type { SpriteImage } from './sprites';

/**
 * The arena in native pixels, drawn at the largest whole scale that fits. It
 * is wide on a big screen and square on a phone, where the wide one would only
 * fit at 1x; there the dinosaur stands further back on the bank, above the boss
 * in the water. `fit()` picks one and sets these.
 */
const WIDE = { w: 300, h: 170, dinoX: 18, feetUp: 1 };
const SQUARE = { w: 180, h: 180, dinoX: 10, feetUp: 30 };
let W = WIDE.w;
let H = WIDE.h;
/** The stream, one water strip deep as in the yard: its top row, and the row a swimmer (or the boss) breaks the surface on. */
let WATER_TOP = H - 24;
let WATERLINE = WATER_TOP + 9;
let DINO_X = WIDE.dinoX;
/** The row a walking dinosaur's feet stand on. */
let DINO_FEET = WATER_TOP - WIDE.feetUp;
/** How much of the boss shows above the water. */
const BOSS_SHOWN = 34;
const DINO_SIZE = 64;

const RED = '#e43b44';
const GOLD = '#fee761';
const WHITE = '#ffffff';
const RIPPLE = '#2ce8f5';
const BANK_WET = '#733e39';
const BANK_EDGE = '#3e2731';
const GROUND = '#3e2731';

export interface BossFightHooks {
  /** The fight is over: settle it in the save, and say what it paid. */
  onDone(fight: Fight, def: DinoDef): FightOutcome;
  play(sound: SoundName): void;
}

export interface BossFightView {
  readonly open: boolean;
  start(boss: BossDef, def: DinoDef, state: GameState): void;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function createBossFight(root: HTMLElement, hooks: BossFightHooks): BossFightView {
  const backdrop = el('div', 'modal bossfight');
  backdrop.hidden = true;
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-label', 'Boss fight');

  // The top line: the dinosaur and its hearts, the clock, the boss and its health.
  const hud = el('div', 'bossfight__hud');
  const youBox = el('div', 'bossfight__who');
  const youName = el('span', 'bossfight__name');
  const hearts = el('span', 'bossfight__hearts');
  youBox.append(youName, hearts);
  const clock = el('div', 'bossfight__clock');
  const bossWho = el('div', 'bossfight__who bossfight__who--boss');
  const bossName = el('span', 'bossfight__name');
  const bar = el('div', 'bossfight__bar');
  const barFill = el('i', '');
  bar.append(barFill);
  bossWho.append(bossName, bar);
  hud.append(youBox, clock, bossWho);

  // The arena, with a layer over it for words: floats, the warning, prompts.
  const stage = el('div', 'bossfight__stage');
  const canvas = el('canvas', 'bossfight__canvas');
  const layer = el('div', 'bossfight__layer');
  const warning = el('div', 'bossfight__warning');
  const prompt = el('div', 'bossfight__prompt');
  const combo = el('div', 'bossfight__combo');
  layer.append(warning, prompt, combo);
  stage.append(canvas, layer);

  const controls = el('div', 'bossfight__controls');
  const braceBtn = el('button', 'modal__button bossfight__brace', 'Brace');
  braceBtn.type = 'button';
  const specialBtn = el('button', 'modal__button bossfight__special');
  specialBtn.type = 'button';
  const fleeBtn = el('button', 'modal__button modal__button--quiet', 'Flee');
  fleeBtn.type = 'button';
  controls.append(braceBtn, specialBtn, fleeBtn);

  const result = el('div', 'bossfight__result');
  result.hidden = true;
  const resultTitle = el('h2', 'bossfight__verdict');
  const resultLines = el('ul', 'bossfight__spoils');
  const back = el('button', 'modal__button', 'Back to the yard');
  back.type = 'button';
  result.append(resultTitle, resultLines, back);

  const inner = el('div', 'bossfight__inner');
  inner.append(hud, stage, controls, result);
  backdrop.append(inner);
  root.append(backdrop);

  const ctx = canvas.getContext('2d');
  let scale = 2;
  let render = 2;
  let open = false;
  let fight: Fight | null = null;
  let dino: DinoDef | null = null;
  let viewState: GameState | null = null;
  let bossArt: HTMLImageElement | null = null;
  let raf = 0;
  let lastFrame = 0;
  let acc = 0;
  /** Ticks left on the "FIGHT!" card before the clock starts. */
  let intro = 0;
  /** Animation counters, in ticks. */
  let hurtTicks = 0;
  let bossHurtTicks = 0;
  let lungeTicks = 0;
  let frame = 0;

  const bossArtCache = new Map<string, HTMLImageElement>();
  function loadBossArt(boss: BossDef): HTMLImageElement {
    let img = bossArtCache.get(boss.art);
    if (!img) {
      img = new Image();
      img.src = `${import.meta.env.BASE_URL}art/bosses/${boss.art}.png`;
      bossArtCache.set(boss.art, img);
    }
    return img;
  }

  /** The largest whole scale at which the arena fits beside the HUD and buttons. */
  function fit(): void {
    const availW = Math.min(window.innerWidth - 24, 1100);
    const availH = window.innerHeight - 190;
    const scaleFor = (a: { w: number; h: number }): number =>
      Math.max(1, Math.min(4, Math.floor(Math.min(availW / a.w, availH / a.h))));
    const layout = scaleFor(SQUARE) > scaleFor(WIDE) ? SQUARE : WIDE;
    W = layout.w;
    H = layout.h;
    WATER_TOP = H - 24;
    WATERLINE = WATER_TOP + 9;
    DINO_X = layout.dinoX;
    DINO_FEET = WATER_TOP - layout.feetUp;
    scale = scaleFor(layout);
    render = scale * Math.max(1, Math.floor(window.devicePixelRatio || 1));
    canvas.width = W * render;
    canvas.height = H * render;
    canvas.style.width = `${W * scale}px`;
    canvas.style.height = `${H * scale}px`;
    stage.style.width = `${W * scale}px`;
    inner.style.setProperty('--arena-w', `${W * scale}px`);
  }

  // ---- geometry ------------------------------------------------------------

  function bossBox(): { x: number; y: number; w: number; h: number } | null {
    if (!bossArt?.complete || !bossArt.naturalWidth) return null;
    const w = bossArt.naturalWidth;
    return { x: W - w - 6, y: WATERLINE - BOSS_SHOWN, w, h: BOSS_SHOWN };
  }

  function dinoSprite(): SpriteImage | null {
    if (!dino || !viewState) return null;
    return sizedDinoSprite(spriteIdFor(dino, viewState), artSizeOf(dino, 'full'), DINO_SIZE);
  }

  function rippleX(frac: number): number {
    const left = W * 0.36;
    return Math.round(left + frac * (W - 14 - left));
  }

  // ---- drawing --------------------------------------------------------------

  function drawArena(g: CanvasRenderingContext2D): void {
    const name = viewState ? (eraModifier(viewState)?.background ?? 'default') : 'default';
    const bg = backgroundImage(name) ?? backgroundImage('default');
    g.fillStyle = backgroundGroundColor(name) ?? GROUND;
    g.fillRect(0, 0, W, H);
    if (bg) {
      // The painting's ground a little above the water, mirrored either side.
      const top = WATER_TOP + 16 - bg.height;
      const cx = Math.floor(W / 2 - bg.width / 2);
      for (let k = -1; k <= 1; k++) {
        const left = cx + k * bg.width;
        if (k === 0) g.drawImage(bg, left, top);
        else {
          g.save();
          g.translate(left + bg.width, 0);
          g.scale(-1, 1);
          g.drawImage(bg, 0, top);
          g.restore();
        }
      }
    }
    const strip = waterImage(name) ?? waterImage('default');
    if (strip) {
      const flow = Math.floor(frame / 8) % strip.width;
      for (let y = WATER_TOP; y < H; y += strip.height) {
        for (let x = -flow; x < W; x += strip.width) g.drawImage(strip, x, y);
      }
    } else {
      g.fillStyle = '#124e89';
      g.fillRect(0, WATER_TOP, W, H - WATER_TOP);
    }
    for (let x = 0; x < W; x++) {
      const lip = Math.max(
        0,
        Math.round(1.5 + Math.sin(x / 9) * 0.9 + Math.sin(x / 23 + 1.7) * 1.1),
      );
      g.fillStyle = backgroundGroundColor(name) ?? GROUND;
      if (lip > 0) g.fillRect(x, WATER_TOP, 1, lip);
      g.fillStyle = BANK_WET;
      g.fillRect(x, WATER_TOP + lip - 1, 1, 1);
      g.fillStyle = BANK_EDGE;
      g.fillRect(x, WATER_TOP + lip, 1, 1);
    }
  }

  function drawDino(g: CanvasRenderingContext2D, f: Fight): void {
    const sprite = dinoSprite();
    if (!sprite || !dino) return;
    const ink = inkBoundsOf(sprite);
    const swims = dino.swims === true;
    const shake = hurtTicks > 0 || f.phase === 'roll' ? (frame % 2 ? 1 : -1) : 0;
    const lunge = lungeTicks > 0 ? Math.round(Math.sin((lungeTicks / 8) * Math.PI) * 24) : 0;
    const x = DINO_X + shake + lunge;
    const y = swims
      ? WATERLINE - Math.round(ink.top + (ink.bottom - ink.top) * 0.5)
      : DINO_FEET - ink.bottom;
    g.save();
    if (swims) {
      g.beginPath();
      g.rect(0, 0, W, WATERLINE);
      g.clip();
    }
    // Sprites face left; the dinosaur faces the boss, to the right.
    g.translate(x + DINO_SIZE, 0);
    g.scale(-1, 1);
    if (bracing(f)) g.drawImage(outlineOf(sprite, GOLD), -1, y - 1, DINO_SIZE + 2, DINO_SIZE + 2);
    const art = hurtTicks > 0 && hurtTicks % 2 ? silhouetteOf(sprite, RED) : sprite;
    g.drawImage(art, 0, y, DINO_SIZE, DINO_SIZE);
    g.restore();
    if (swims) {
      g.fillStyle = RIPPLE;
      g.fillRect(x + (DINO_SIZE - 1 - ink.right) - 2, WATERLINE, ink.right - ink.left + 4, 1);
    }
  }

  function drawBoss(g: CanvasRenderingContext2D, f: Fight): void {
    const box = bossBox();
    if (!box || !bossArt) return;
    if (f.phase === 'submerged') {
      // Only ripples show: rings that swell and fade, one of them the boss.
      for (const r of f.ripples) {
        const cx = rippleX(r);
        const grow = (frame / 3) % 6;
        g.fillStyle = RIPPLE;
        for (const radius of [3 + grow, 9 + grow]) {
          g.fillRect(cx - radius, WATERLINE + 3, radius * 2, 1);
          g.fillRect(cx - radius + 2, WATERLINE + 5, radius * 2 - 4, 1);
        }
      }
      return;
    }
    const winding = f.phase === 'windup';
    const shake = (winding && frame % 4 < 2) || bossHurtTicks > 0 ? (frame % 2 ? 1 : -1) : 0;
    const x = box.x + shake;
    const y = WATERLINE - BOSS_SHOWN;
    g.save();
    g.beginPath();
    g.rect(0, 0, W, WATERLINE);
    g.clip();
    let art: SpriteImage = bossArt;
    if (bossHurtTicks > 0 && bossHurtTicks % 2) art = silhouetteOf(bossArt, WHITE);
    else if (winding && frame % 6 < 3) art = silhouetteOf(bossArt, RED);
    g.drawImage(art, x, y);
    g.restore();
    g.fillStyle = RIPPLE;
    g.fillRect(box.x - 3, WATERLINE, box.w + 6, 1);
    g.fillStyle = WHITE;
    g.fillRect(box.x - 7, WATERLINE, 3, 1);
    g.fillRect(box.x + box.w + 4, WATERLINE, 3, 1);
    // Dazed: three stars circling over its head.
    if (f.dazedTicks > 0) {
      for (let i = 0; i < 3; i++) {
        const a = frame / 5 + (i * Math.PI * 2) / 3;
        g.fillStyle = GOLD;
        g.fillRect(
          Math.round(box.x + 30 + Math.cos(a) * 14),
          Math.round(box.y - 4 + Math.sin(a) * 4),
          2,
          2,
        );
      }
    }
    // The weak point: a gold diamond, blinking as it is about to close.
    if (f.weak && (f.weak.ticksLeft > 8 || frame % 2)) {
      const wx = Math.round(box.x + f.weak.x * box.w);
      const wy = Math.round(box.y + f.weak.y * box.h);
      const diamond = (radius: number, colour: string): void => {
        g.fillStyle = colour;
        for (let r = 0; r <= radius; r++) {
          const half = radius - r;
          g.fillRect(wx - half, wy - r, half * 2 + 1, 1);
          g.fillRect(wx - half, wy + r, half * 2 + 1, 1);
        }
      };
      diamond(5, '#000000');
      diamond(4, GOLD);
      diamond(1, WHITE);
    }
  }

  function draw(): void {
    const f = fight;
    if (!ctx || !f) return;
    ctx.setTransform(render, 0, 0, render, 0, 0);
    ctx.imageSmoothingEnabled = false;
    drawArena(ctx);
    drawBoss(ctx, f);
    drawDino(ctx, f);
  }

  // ---- the words over the arena ---------------------------------------------

  function float(text: string, nx: number, ny: number, cls = ''): void {
    const node = el('span', `bossfight__float ${cls}`, text);
    node.style.left = `${nx * scale}px`;
    node.style.top = `${ny * scale}px`;
    layer.append(node);
    window.setTimeout(() => node.remove(), 900);
  }

  function renderHud(f: Fight): void {
    const secs = Math.max(0, Math.ceil(f.ticksLeft / TICKS_PER_SECOND));
    clock.textContent = `0:${String(secs).padStart(2, '0')}`;
    clock.classList.toggle('bossfight__clock--enraged', f.enraged);
    stage.classList.toggle('bossfight__stage--enraged', f.enraged && f.phase !== 'over');
    barFill.style.width = `${(f.bossHp / f.boss.health) * 100}%`;
    hearts.textContent = '♥'.repeat(f.hearts) + '♡'.repeat(Math.max(0, HEARTS - f.hearts));
    const ready = specialReady(f);
    const name = SPECIALS[f.special].name;
    specialBtn.textContent = ready ? `${name} · ready` : name;
    specialBtn.disabled = !ready;
    specialBtn.style.setProperty('--meter', `${(f.meter / METER_FULL) * 100}%`);
    braceBtn.disabled = f.phase === 'over';
    combo.textContent =
      f.combo >= 3 && f.phase !== 'over'
        ? `COMBO ×${f.combo}${f.combo >= COMBO_MAX ? ' MAX' : ''}`
        : '';
    if (f.phase === 'windup' && f.move) {
      const move = f.boss.moves.find((m) => m.id === f.move);
      warning.textContent = `! ${move?.name ?? ''}`;
    } else if (f.phase === 'submerged') {
      warning.textContent = 'Which ripple?';
    } else warning.textContent = '';
    if (intro > 0) prompt.textContent = 'FIGHT!';
    else if (f.phase === 'roll') prompt.textContent = `Tap to break free! ${f.rollTaps}`;
    else prompt.textContent = '';
  }

  function onEvent(e: FightEvent, f: Fight): void {
    const box = bossBox();
    const bx = box ? box.x + box.w * 0.35 : W * 0.7;
    const by = box ? box.y : WATERLINE - 30;
    switch (e.kind) {
      case 'hit':
        bossHurtTicks = e.crit ? 4 : 2;
        float(
          e.crit ? `CRIT ${formatEggs(e.damage)}` : formatEggs(e.damage),
          bx + Math.random() * 60,
          by - 4,
          e.crit ? 'bossfight__float--crit' : '',
        );
        hooks.play(e.crit ? 'crack' : 'click');
        break;
      case 'blocked':
        float(
          e.reflected > 0 ? `BLOCKED · ${formatEggs(e.reflected)} back` : 'BLOCKED',
          DINO_X + 10,
          DINO_FEET - 70,
          'bossfight__float--block',
        );
        hooks.play('clink');
        break;
      case 'hurt':
        hurtTicks = 8;
        hooks.play('thud');
        break;
      case 'windup':
        hooks.play('whiff');
        break;
      case 'escaped':
        float('FREE!', DINO_X + 10, DINO_FEET - 70, 'bossfight__float--block');
        break;
      case 'found':
        float('FOUND IT · DAZED', bx, by - 10, 'bossfight__float--crit');
        hooks.play('crack');
        break;
      case 'special':
        lungeTicks = 8;
        bossHurtTicks = 6;
        if (e.damage > 0)
          float(
            `${SPECIALS[e.special].name.toUpperCase()} ${formatEggs(e.damage)}`,
            bx,
            by - 14,
            'bossfight__float--crit',
          );
        else
          float(
            SPECIALS[e.special].name.toUpperCase(),
            DINO_X + 10,
            DINO_FEET - 70,
            'bossfight__float--block',
          );
        hooks.play('crash');
        break;
      case 'over':
        end(f);
        break;
      default:
        break;
    }
  }

  // ---- the loop ---------------------------------------------------------------

  function step(): void {
    const f = fight;
    if (!f) return;
    frame++;
    if (hurtTicks > 0) hurtTicks--;
    if (bossHurtTicks > 0) bossHurtTicks--;
    if (lungeTicks > 0) lungeTicks--;
    if (intro > 0) {
      intro--;
      return;
    }
    tickFight(f);
    for (const e of drainEvents(f)) onEvent(e, f);
  }

  function loop(now: number): void {
    if (!open) return;
    const dt = Math.min(250, now - lastFrame);
    lastFrame = now;
    acc += dt;
    const tickMs = 1000 / TICKS_PER_SECOND;
    while (acc >= tickMs) {
      acc -= tickMs;
      step();
    }
    if (fight) {
      draw();
      renderHud(fight);
    }
    raf = requestAnimationFrame(loop);
  }

  function end(f: Fight): void {
    if (!dino) return;
    const outcome = hooks.onDone(f, dino);
    const won = f.result === 'won';
    hooks.play(won ? 'achievement' : 'thud');
    resultTitle.textContent = won
      ? outcome.flawless
        ? 'FLAWLESS VICTORY'
        : 'VICTORY'
      : 'DEFEATED';
    resultTitle.classList.toggle('bossfight__verdict--won', won);
    const who = displayName();
    const secs = Math.round((f.boss.seconds * TICKS_PER_SECOND - f.ticksLeft) / TICKS_PER_SECOND);
    const lines: string[] = [];
    if (won) {
      lines.push(`${f.boss.name} beaten in ${secs} seconds.`);
      if (outcome.title) lines.push(`${who} is now the ${outcome.title}.`);
      if (outcome.roar)
        lines.push('Victory Roar: the whole herd lays twice as much for 10 minutes.');
      lines.push(
        `+${outcome.shards} meteor shards${outcome.flawless ? ' (doubled: not a scratch)' : ''}.`,
      );
      if (outcome.fossils > 0)
        lines.push(
          `+${outcome.fossils} fossils: the first time ${f.boss.name} has ever been beaten.`,
        );
    } else {
      lines.push(
        f.hearts === 0 ? `${who} was beaten.` : f.ticksLeft <= 0 ? 'Time ran out.' : `${who} fled.`,
      );
      lines.push(
        `${f.boss.name} had ${Math.round((f.bossHp / f.boss.health) * 100)}% of its health left.`,
      );
    }
    lines.push(`${who} rests for an hour before fighting again.`);
    resultLines.replaceChildren(...lines.map((l) => el('li', '', l)));
    controls.hidden = true;
    result.hidden = false;
    back.focus();
  }

  function displayName(): string {
    if (!dino || !viewState) return '';
    const pet = viewState.names[dino.id];
    return pet ? `"${pet}"` : `The ${speciesName(dino, viewState)}`;
  }

  function close(): void {
    open = false;
    cancelAnimationFrame(raf);
    backdrop.hidden = true;
    for (const n of [...layer.querySelectorAll('.bossfight__float')]) n.remove();
    fight = null;
  }

  // ---- input -----------------------------------------------------------------

  canvas.addEventListener('pointerdown', (ev) => {
    const f = fight;
    if (!f || intro > 0 || f.phase === 'over') return;
    ev.preventDefault();
    const r = canvas.getBoundingClientRect();
    const nx = (ev.clientX - r.left) / scale;
    const ny = (ev.clientY - r.top) / scale;
    if (f.phase === 'roll') {
      tapBoss(f, false);
      return;
    }
    if (f.phase === 'submerged') {
      let pick: number | null = null;
      f.ripples.forEach((frac, i) => {
        if (Math.abs(nx - rippleX(frac)) <= 14 && Math.abs(ny - (WATERLINE + 4)) <= 12) pick = i;
      });
      tapRipple(f, pick);
      for (const e of drainEvents(f)) onEvent(e, f);
      return;
    }
    const box = bossBox();
    if (!box) return;
    const slop = 4;
    if (nx < box.x - slop || nx > box.x + box.w + slop || ny < box.y - slop || ny > WATERLINE + 2)
      return;
    let onWeak = false;
    if (f.weak) {
      const wx = box.x + f.weak.x * box.w;
      const wy = box.y + f.weak.y * box.h;
      onWeak = Math.abs(nx - wx) <= 7 && Math.abs(ny - wy) <= 7;
    }
    tapBoss(f, onWeak);
    for (const e of drainEvents(f)) onEvent(e, f);
  });
  braceBtn.addEventListener('click', () => {
    if (fight && intro === 0) brace(fight);
  });
  specialBtn.addEventListener('click', () => {
    if (!fight) return;
    useSpecial(fight);
    for (const e of drainEvents(fight)) onEvent(e, fight);
  });
  fleeBtn.addEventListener('click', () => {
    if (!fight) return;
    flee(fight);
    for (const e of drainEvents(fight)) onEvent(e, fight);
  });
  back.addEventListener('click', close);
  window.addEventListener('keydown', (ev) => {
    if (!open || !fight || fight.phase === 'over') return;
    if (ev.key === ' ' || ev.key === 'b' || ev.key === 'B') {
      ev.preventDefault();
      if (intro === 0) brace(fight);
    } else if (ev.key === 's' || ev.key === 'S') {
      useSpecial(fight);
      for (const e of drainEvents(fight)) onEvent(e, fight);
    }
  });
  window.addEventListener('resize', () => {
    if (open) fit();
  });

  return {
    get open() {
      return open;
    },
    start(boss, def, state) {
      // One fight at a time: a second start would run a second loop beside
      // the first, and the fight would play at double speed.
      if (open) return;
      viewState = state;
      dino = def;
      bossArt = loadBossArt(boss);
      fight = startFight(boss, {
        id: def.id,
        // 0 for the first tier, as fightPower counts.
        tier: def.tier - 1,
        level: state.dinos[def.id] ?? 0,
        evolved: isEvolved(def, state),
      });
      const pet = state.names[def.id];
      youName.textContent = `${pet ? `"${pet}" ` : ''}${speciesName(def, state)} · Lvl ${state.dinos[def.id] ?? 0}`;
      bossName.textContent = `${boss.name}, ${boss.epithet}`;
      hurtTicks = bossHurtTicks = lungeTicks = 0;
      intro = Math.round(1.2 * TICKS_PER_SECOND);
      acc = 0;
      result.hidden = true;
      controls.hidden = false;
      open = true;
      backdrop.hidden = false;
      fit();
      renderHud(fight);
      lastFrame = performance.now();
      raf = requestAnimationFrame(loop);
    },
  };
}
